// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  authenticateWithWeb3Signature,
  connectInjectedEvmWallet,
  fetchWeb3AuthSession,
  logoutWeb3Session,
  fetchWeb3AuthNonce,
  signWeb3AuthMessage,
} from '../util/web3Auth';
import { isUserAuthorized } from '../util/userHelpers';
import { logout as logoutSharetribe } from '../ducks/auth.duck';

export type AuthMode = 'none' | 'web2' | 'web3';

export type WalletSessionState = {
  walletAddress: string | null;
  sessionExpiresAt: string | null;
};

const WalletSessionContext = createContext(null);

const formatAddress = address =>
  address ? `${address.slice(0, 6)}…${address.slice(-4)}` : '';

export const WalletSessionProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isSharetribeAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const currentUser = useSelector(state => state.user?.currentUser);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connectInProgress, setConnectInProgress] = useState(false);
  const [connectError, setConnectError] = useState(null);

  const refreshSession = useCallback(async () => {
    try {
      const data = await fetchWeb3AuthSession();
      if (data?.authenticated && data?.identity?.walletAddress) {
        setSession({
          walletAddress: data.identity.walletAddress,
          sessionExpiresAt: data.sessionExpiresAt || null,
        });
        return true;
      }
      setSession(null);
      return false;
    } catch {
      setSession(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession, isSharetribeAuthenticated]);

  const authMode = useMemo(() => {
    if (isSharetribeAuthenticated && isUserAuthorized(currentUser)) {
      return 'web2';
    }
    if (session?.walletAddress) {
      return 'web3';
    }
    return 'none';
  }, [isSharetribeAuthenticated, currentUser, session?.walletAddress]);

  const connectWalletSession = useCallback(async () => {
    if (authMode === 'web2') {
      const error = new Error(
        'Sign out of your account first to connect a wallet-only session.'
      );
      error.code = 'WEB2_ACTIVE';
      throw error;
    }

    setConnectError(null);
    setConnectInProgress(true);

    try {
      const { provider, walletAddress, chain } = await connectInjectedEvmWallet();
      const { nonce, message } = await fetchWeb3AuthNonce({ walletAddress, chain });
      const signature = await signWeb3AuthMessage({ provider, message });
      await authenticateWithWeb3Signature({ walletAddress, signature, nonce, chain });
      await refreshSession();
      return walletAddress;
    } catch (e) {
      const message =
        e.code === 'NO_WALLET'
          ? 'No EVM wallet detected. Install MetaMask or Rabby.'
          : e.message || 'Wallet connection failed';
      setConnectError(message);
      throw e;
    } finally {
      setConnectInProgress(false);
    }
  }, [authMode, refreshSession]);

  const disconnectWalletSession = useCallback(async () => {
    setConnectError(null);
    try {
      await logoutWeb3Session();
    } catch {
      // Clear local state even if cookie clear fails
    }
    setSession(null);
  }, []);

  const logoutWeb2Session = useCallback(async () => {
    await dispatch(logoutSharetribe());
  }, [dispatch]);

  const value = useMemo(
    () => ({
      authMode,
      session,
      walletAddress: session?.walletAddress || null,
      walletLabel: formatAddress(session?.walletAddress),
      loading,
      connectInProgress,
      connectError,
      refreshSession,
      connectWalletSession,
      disconnectWalletSession,
      logoutWeb2Session,
      isWalletAuthenticated: authMode === 'web3',
      isWeb2Authenticated: authMode === 'web2',
      isGuest: authMode === 'none',
    }),
    [
      authMode,
      session,
      loading,
      connectInProgress,
      connectError,
      refreshSession,
      connectWalletSession,
      disconnectWalletSession,
      logoutWeb2Session,
    ]
  );

  return (
    <WalletSessionContext.Provider value={value}>{children}</WalletSessionContext.Provider>
  );
};

export const useWalletSession = () => {
  const ctx = useContext(WalletSessionContext);
  if (!ctx) {
    throw new Error('useWalletSession must be used within WalletSessionProvider');
  }
  return ctx;
};

/** Safe hook for components that may render outside the provider (e.g. tests). */
export const useWalletSessionMaybe = () => useContext(WalletSessionContext);

export default WalletSessionContext;
